import axios, { AxiosResponse } from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { Block, StudyGuideBlockContent } from 'src/config/api/models/Block';
import { store } from 'src/modules/shared/store';
import { ClientSettings, Exercise, ExerciseTool } from 'src/config/api/models/Exercise';
import apiRoutes from 'src/config/api/routes';

export const fetchBlocks = createAsyncThunk('blocks/fetchBlocks', async (pageId: string) => {
  const { jwt } = store.getState().auth;

  const response: AxiosResponse<{ data: Block[] }> = await axios({
    url: apiRoutes.blocks(pageId),
    headers: {
      Authorization: `Bearer ${jwt}`,
    },
  });

  return response.data.data;
});

export const reorderBlocks = createAsyncThunk(
  'blocks/reorderBlocks',
  async ({ bookId, pageId, newBlockList }: reorderBlocksType) => {
    const { jwt } = store.getState().auth;
    // const listIds = mapBlocksListToIds(newBlockList);

    const response: AxiosResponse<{ data: { blocks: string[]; rest: any } }> = await axios({
      url: apiRoutes.orderBlocks(bookId, pageId),
      method: 'PATCH',
      data: newBlockList,
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    });
    return response.data.data.blocks;
  }
);

export const fetchExercises = createAsyncThunk('blocks/fetchExercises', async (pageId: string) => {
  const { jwt } = store.getState().auth;

  const response: AxiosResponse<{ data: Exercise[] }> = await axios({
    url: apiRoutes.blocks(pageId),
    headers: {
      Authorization: `Bearer ${jwt}`,
    },
  });

  return response.data.data;
});

type patchBlockType = {
  bookId: string;
  pageId: string;
  blockId: string;
  dontUpdateBlocks?: boolean;
  payload?:
    | Record<string, any>
    | Block
    | string
    | { content: StudyGuideBlockContent }
    | { client_settings: ClientSettings }
    | undefined;
};

export type reorderBlocksType = {
  bookId: string;
  pageId: string;
  newBlockList: any; //Block[];
};

export type addBlockType = {
  bookId: string;
  pageId: string;
  payload: {
    type: string;
    title?: string;
    content?: string | StudyGuideBlockContent;
    level?: string;
    bloom_type?: string;
    algebra_kit_exercise_id?: string;
    amountOfColumns?: number;
    previous_block_id?: string;
    exercise_tool?: ExerciseTool;
    cluster_id?: string;
    section_id?: string;
    description?: string;
  };
  isExercisePage?: boolean;
};

type patchClusterType = {
  clusterId: string;
  payload?:
    | Record<string, any>
    | string
    | { content: StudyGuideBlockContent }
    | { client_settings: ClientSettings }
    | undefined;
};

type createClusterType = {
  beforeBlockId: string;
  pageId: string;
};

type splitClusterType = {
  clusterId: string;
  beforeBlockId: string;
  pageId: string;
};

type createSectionType = {
  beforeBlockId: string;
  clusterId: string;
};

type defaultBlockParams = {
  bookId: string;
  pageId: string;
  blockId: string;
  cluster_id?: string;
  section_id?: string;
};

export const addBlock = createAsyncThunk(
  'blocks/add',
  async ({ bookId, pageId, payload, isExercisePage = false }: addBlockType) => {
    const { jwt } = store.getState().auth;
    const response: AxiosResponse<{ data: Block | Exercise }> = await axios({
      url: apiRoutes.addBlock(bookId, pageId),
      method: 'POST',
      data: payload,
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    });
    return {
      ...response.data.data,
      isExercisePage,
      cluster_id: payload.cluster_id,
      section_id: payload.section_id,
      previous_block_id: payload.previous_block_id,
    };
  }
);

export const deleteBlock = createAsyncThunk(
  'blocks/delete',
  async ({ bookId, pageId, blockId, cluster_id, section_id }: defaultBlockParams) => {
    const { jwt } = store.getState().auth;
    await axios({
      url: apiRoutes.block(bookId, pageId, blockId),
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    });
    return { id: blockId, cluster_id, section_id };
  }
);

export const toggleBlockIsPrintable = createAsyncThunk(
  'blocks/toggleBlockIsPrintable',
  async ({ blockId }: defaultBlockParams) => {
    const { jwt } = store.getState().auth;
    const response: AxiosResponse<{ data: Block }> = await axios({
      url: apiRoutes.toggleBlockIsPrintable(blockId),
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    });
    return response.data.data;
  }
);

export const patchBlock = createAsyncThunk(
  'blocks/patchBlock',
  async ({ bookId, pageId, blockId, payload }: patchBlockType) => {
    const { jwt } = store.getState().auth;
    const response: AxiosResponse<{ data: Block | Exercise }> = await axios({
      url: apiRoutes.block(bookId, pageId, blockId),
      method: 'PATCH',
      data: payload,
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    });
    return response.data.data;
  }
);

export const patchCluster = createAsyncThunk(
  'blocks/patchCluster',
  async ({ clusterId, payload }: patchClusterType) => {
    const { jwt } = store.getState().auth;
    const response: AxiosResponse<{ data: Block }> = await axios({
      url: apiRoutes.cluster(clusterId),
      method: 'PATCH',
      data: payload,
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    });
    return response.data.data;
  }
);

export const createCluster = createAsyncThunk(
  'blocks/createCluster',
  async ({ beforeBlockId, pageId }: createClusterType) => {
    const { jwt } = store.getState().auth;
    const response: AxiosResponse<{ data: Block }> = await axios({
      url: apiRoutes.clusterCreate(),
      method: 'POST',
      data: { beforeBlockId, pageId },
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    });
    return { ...response.data.data, previousBlockId: beforeBlockId };
  }
);

export const createSection = createAsyncThunk(
  'blocks/createSection',
  async ({ beforeBlockId, clusterId }: createSectionType) => {
    const { jwt } = store.getState().auth;
    const response: AxiosResponse<{ data: Block }> = await axios({
      url: apiRoutes.sectionCreate(),
      method: 'POST',
      data: { beforeBlockId, clusterId },
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    });
    return { ...response.data.data, previousBlockId: beforeBlockId };
  }
);

export const splitCluster = createAsyncThunk(
  'blocks/splitCluster',
  async ({ beforeBlockId, pageId, clusterId }: splitClusterType) => {
    const { jwt } = store.getState().auth;
    const response: AxiosResponse<{ data: { oldCluster: Block; newCluster: Block } }> = await axios(
      {
        url: apiRoutes.clusterSplit(clusterId),
        method: 'POST',
        data: { beforeBlockId, pageId },
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      }
    );
    return { ...response.data.data, previousBlockId: beforeBlockId, clusterId };
  }
);

export const deleteCluster = createAsyncThunk(
  'blocks/deleteCluster',
  async ({ clusterId }: { clusterId: string }) => {
    const { jwt } = store.getState().auth;
    await axios({
      url: apiRoutes.cluster(clusterId),
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    });
    return { id: clusterId };
  }
);

export const deleteSection = createAsyncThunk(
  'blocks/deleteSection',
  async ({ clusterId, sectionId }: { clusterId: string; sectionId: string }) => {
    const { jwt } = store.getState().auth;
    await axios({
      url: apiRoutes.sectionDelete(clusterId, sectionId),
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    });
    return { id: clusterId };
  }
);

export const releaseCluster = createAsyncThunk(
  'blocks/releaseCluster',
  async ({ clusterId, pageId }: { clusterId: string; pageId: string }) => {
    const { jwt } = store.getState().auth;
    await axios({
      url: apiRoutes.clusterRelease(clusterId),
      method: 'POST',
      data: { pageId },
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    });
    return { id: clusterId };
  }
);
